import { Box } from '@mui/material'
import { getCSvConfigDialoDispAndCache, downloadCsvFile } from 'api/csv'
import { getDetail as getDetailAPI } from 'api/documents'
import CsvConfigDialog from 'components/AppDialogs/CsvConfigDialog'
import DelDocDialog from 'components/AppDialogs/DelDocDialog'
import RenameDocDialog from 'components/AppDialogs/RenameDocDialog'
import SendDocDialog from 'components/AppDialogs/SendDocDialog'
import GeneralOcrEditor from 'components/DocumentDetail/Editor/GeneralOcr/GeneralOcr'
import InvoiceEditor from 'components/DocumentDetail/Editor/Invoice/Invoice'
import OcrBuilderEditor from 'components/DocumentDetail/Editor/OcrBuilder/OcrBuilder'
import OrderFormEditor from 'components/DocumentDetail/Editor/OrderForm/OrderForm'
import VerifyEditor from 'components/DocumentDetail/Editor/Verify/VerifyEditor'
import DeletedHeader from 'components/DocumentDetail/Header/Deleted'
import Header from 'components/DocumentDetail/Header/General'
import VerifyHeader from 'components/DocumentDetail/Header/Verify'
import MultiDocList from 'components/DocumentDetail/MultiDocList'
import Viewer from 'components/DocumentDetail/Viewer/Viewer'
import LoadingWrapper from 'components/LoadingWrapper'
import { useRootLoaderData } from 'components/Root.loader'
import { DOC_TYPE } from 'config'
import { Document } from 'entities/Documents'
import { DocumentGeneral } from 'entities/DocumentsGeneral'
import { DocumentInvoiceV2 } from 'entities/DocumentsInvoiceV2'
import { DocumentBuilder } from 'entities/DocumentsOcrBuilder'
import { DocumentOrderForm } from 'entities/DocumentsOrderForm'
import { GeneralOcrTableRefsProvider } from 'hooks/GeneralOcrTableRefContextProvider'
import HighlightContextProvider from 'hooks/HighlightContextProvider'
import { TabProvider } from 'hooks/TabContextProvider'
import { useAlert, useConfirm } from 'hooks/useDialog'
import useLeavingPrompt from 'hooks/useLeavingPrompt'
import useVendors from 'hooks/useVendors'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isEmpty } from 'utils/utils'

import CurrentPreview from './CurrentPreview'

function DocumentDetail() {
  const rootData = useRootLoaderData()
  // const { mode } = useMode()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { useConcur } = rootData.userGroupConfig
  const { vendors, vendorNotFound } = useVendors(useConcur)

  const ids = searchParams.getAll('id')
  const oidsKey = searchParams.get('key')
  const idsFromKeyStr = oidsKey ? localStorage.getItem(oidsKey) : ''
  const idsFromKey = useMemo(() => idsFromKeyStr?.split(','), [idsFromKeyStr])
  // objectIDs: url params
  const objectIDs = useMemo(
    () => (ids.length === 0 ? idsFromKey || ['not possible'] : ids),
    [ids, idsFromKey],
  )
  // sortedIDS: 実際に読み込んだ一覧
  const [sortedIDs, setSortedIDs] = useState<string[] | null>(ids.length > 0 ? ids : null)
  const isMulti = sortedIDs ? sortedIDs.length > 1 : objectIDs.length > 1

  // nameChanged: increments when some has been changed
  // this value is used to infor multi doc list to reload
  const [changed, setChanged] = useState(0)
  const [docDetail, setDocDetail] = useState<Document | null>(null)
  const [renamedName, setRenamedName] = useState<string | undefined>(undefined)
  const [oIdx, setOIdx] = useState<number>(0)
  const objectID = sortedIDs?.[oIdx]
  const docInfo = docDetail?.docInfo
  const deleted = docDetail?.isDeleted
  const useVerifyEditor = docDetail?.canConfirm()

  const confirm = useConfirm()
  const alert = useAlert()
  const [openDelDialog, setOpenDelDialog] = useState(false)
  const [openSendDialog, setOpenSendDialog] = useState(false)
  const [renameDialog, setRenameDialog] = useState({ open: false, objectID: '' })
  const [openCsvConfigDialog, setOpenCsvConfigDialog] = useState<'fromMulti' | boolean>(false)

  const setOpenRenameDialog = (x: boolean) => setRenameDialog((s) => ({ ...s, open: x }))

  const form = useForm({
    mode: 'all',
    // TODO: any
    defaultValues: docDetail?.ocrEditResult as any,
  })
  const { getValues, handleSubmit, reset } = form

  useEffect(() => {
    if (vendorNotFound) {
      alert({
        message: [
          'Concur Invoiceから取引先情報を取得中です。処理完了までお待ち下さい。',
          '処理状況は、システム管理者権限のユーザーアカウントで 設定画面から確認ができます。',
        ],
      }).then(() => navigate(-1))
    }
  }, [vendorNotFound, alert, navigate])

  const nullifyDoc = useCallback(() => {
    setDocDetail(null)
    setRenamedName(undefined)
  }, [])

  const reload = useCallback(async () => {
    if (!objectID) return
    nullifyDoc()
    try {
      const doc = await getDetailAPI(objectID)

      // TODO: refactoring: classを用いて根本からこういうif文を治す
      if (doc.isOrderForm) {
        const orderFormDoc = doc as DocumentOrderForm
        if (!orderFormDoc.ocrEditResult.itemLines) orderFormDoc.ocrEditResult.itemLines = []
      }
      if (doc.isInvoice) {
        const invoiceDoc = doc as DocumentInvoiceV2
        if (!invoiceDoc.ocrEditResult.itemLines) invoiceDoc.ocrEditResult.itemLines = []
        invoiceDoc.ocrEditResult.itemLines.forEach(
          (d, i, a) => (a[i].expenseTypeCodeDumb = d.expenseTypeCode),
        )
        invoiceDoc.config.itemLines.expenseTypeCodeDumb = {
          ...invoiceDoc.config.itemLines.expenseTypeCode,
          edit: false,
        }
      }
      setDocDetail(doc)
    } catch (e) {
      console.error(e)
      await alert({ message: '読み込みに失敗しました' })
      navigate(-1)
    }
  }, [objectID, alert, navigate, nullifyDoc])

  useEffect(() => {
    if (docDetail) {
      reset(docDetail.ocrEditResult)
    }
  }, [docDetail, reset])

  useEffect(() => {
    reload()
  }, [reload])

  useLeavingPrompt({
    message: ['このページから移動しますか？', '入力したデータは保存されません。'],
    when: !isEmpty(form.formState.dirtyFields),
  })

  const save = useCallback(
    async (edit) => {
      await docDetail!.save(edit)
      reload()
    },
    [docDetail, reload],
  )

  const handleSave = async () => {
    // skip validation
    const edit = getValues()
    try {
      await save(edit)
      await alert({ message: '保存しました' })
    } catch {
      await alert({ message: '保存に失敗しました' })
    }
  }

  const doSaveAndDownload = async (fromMulti = false) => {
    // TODO: show loading?
    if (docDetail!.canSave() && !fromMulti) {
      const edit = getValues()
      try {
        await save(edit)
      } catch {
        return await alert({ message: '保存に失敗したためキャンセルしました' })
      }
    }

    try {
      await downloadCsvFile(fromMulti ? sortedIDs! : [objectID!])
    } catch {
      alert({ message: 'ダウンロードに失敗しました。' })
    }
    reload() // 文書 status reload
  }

  const onDownload = async (fromMulti = false) => {
    // skip validation
    if (docDetail!.isVerify && !docDetail!.isFinished) {
      const { status } = await confirm({
        message: 'ベリファイチェック中の為、確認前のデータが含まれる可能性があります',
      })
      if (status === 'canceled') {
        return
      }
    }

    let csvConfigDialogDisp = true
    try {
      csvConfigDialogDisp = await getCSvConfigDialoDispAndCache(
        docInfo!.documentType,
        docInfo!.isVerify ? docInfo!.subGroup : undefined,
        docInfo!.ocrBuilderConfigID,
      )
    } catch {
      return alert({ message: 'csv設定取得失敗しました。' })
    }

    if (csvConfigDialogDisp && docInfo!.documentType !== DOC_TYPE.GENERAL_OCR) {
      setOpenCsvConfigDialog(fromMulti ? 'fromMulti' : true) // doSaveAndDownload() will be called by dialog
    } else {
      const { status } = await confirm({
        message: [
          '選択した文書の結果ファイルをダウンロードしますか？',
          '出力設定は設定画面より変更可能です',
        ],
      })
      if (status === 'canceled') {
        return
      }
      doSaveAndDownload(fromMulti)
    }
  }

  useEffect(() => {
    const savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = savedOverflow
    }
  }, [])

  const send = handleSubmit(
    async (data) => {
      await save(data)
      setOpenSendDialog(true)
    },
    () => {
      alert({ message: '無効な入力です' })
    },
  )

  const handleSend = () => {
    try {
      send()
    } catch {
      alert({ message: '送信に失敗しました' })
    }
  }

  const afterDeletion = useCallback(
    (delIDs: string[]) => {
      if (delIDs.length < 1) return
      if (objectIDs.length === 1 || sortedIDs?.length === 1 || !oidsKey)
        return (reset(), navigate(-1))
      setChanged((c) => c + 1) // = reload multidoc
    },
    [objectIDs, sortedIDs, navigate, oidsKey, reset],
  )

  const headerComp = (() => {
    if (!docDetail) return undefined
    if (deleted) {
      return <DeletedHeader docDetail={docDetail} setOpenDelDialog={setOpenDelDialog} />
    }
    if (docDetail.isVerify) {
      return (
        <VerifyHeader
          docDetail={docDetail}
          onSave={handleSave}
          onSend={handleSend}
          onDownload={onDownload}
          setOpenDelDialog={setOpenDelDialog}
          reload={reload}
        />
      )
    }
    return (
      <Header
        docDetail={docDetail}
        onSave={handleSave}
        onSend={handleSend}
        onDownload={onDownload}
        objIDs={sortedIDs}
        currentIdx={oIdx}
        setOIdx={setOIdx}
        setOpenDelDialog={setOpenDelDialog}
        openRenameDialog={() => setOpenRenameDialog(true)}
        renamedName={renamedName}
      />
    )
  })()

  const vendorsLoaded = !useConcur || vendors.length > 0

  return (
    <LoadingWrapper loaded={vendorsLoaded}>
      <FormProvider {...form}>
        <Box style={{ display: 'flex', height: '100%' }} sx={{ flexDirection: 'row' }}>
          {isMulti && (
            <MultiDocList
              objIDs={objectIDs}
              sortedIDs={sortedIDs}
              objectID={objectID}
              currentIdx={oIdx}
              onDownload={onDownload}
              downloading={false}
              changed={changed}
              setSortedIDs={setSortedIDs}
              setOIdx={setOIdx}
            />
          )}

          <Box style={{ position: 'relative', width: '100%', overflow: 'hiddne' }}>
            <LoadingWrapper loaded={docDetail !== null}>
              {headerComp}
              <TabProvider>
                <HighlightContextProvider>
                  <GeneralOcrTableRefsProvider>
                    <Box
                      style={{
                        display: 'grid',
                        height: '100%',
                        paddingTop: 64,
                        gridTemplateColumns: 'minmax(0, 50%) minmax(650px, 50%)',
                      }}
                    >
                      <div style={{ overflow: 'hidden' }}>
                        <Viewer key={objectID} detail={docDetail!} />
                      </div>
                      {useVerifyEditor ? (
                        <VerifyEditor key={objectID} docDetail={docDetail!} />
                      ) : (
                        <Box
                          style={{
                            display: 'flex',
                            flexFlow: 'column nowrap',
                            justifyContent: 'space-between',
                            height: '100%',
                            overflow: 'hidden',
                          }}
                        >
                          {docDetail &&
                            (docDetail.isInvoice ? (
                              <InvoiceEditor
                                key={objectID}
                                vendors={vendors}
                                docDetail={docDetail as DocumentInvoiceV2}
                              />
                            ) : docDetail.isOrderForm ? (
                              <OrderFormEditor
                                key={objectID}
                                docDetail={docDetail as DocumentOrderForm}
                              />
                            ) : docDetail.isOcrBuilder ? (
                              <OcrBuilderEditor
                                key={objectID}
                                docDetail={docDetail as DocumentBuilder}
                              />
                            ) : (
                              <GeneralOcrEditor
                                key={objectID}
                                docDetail={docDetail as DocumentGeneral}
                              />
                            ))}
                          {docDetail && !docDetail.isGeneral && (
                            <CurrentPreview detail={docDetail} />
                          )}
                        </Box>
                      )}
                    </Box>
                  </GeneralOcrTableRefsProvider>
                </HighlightContextProvider>
              </TabProvider>
            </LoadingWrapper>
          </Box>
        </Box>
      </FormProvider>
      {objectID && docDetail && (
        <>
          <DelDocDialog
            open={openDelDialog}
            setOpenDialog={setOpenDelDialog}
            selectedIDs={[objectID]}
            hard={deleted}
            reloadDocs={afterDeletion}
          />
          <SendDocDialog
            open={openSendDialog}
            setOpenDialog={setOpenSendDialog}
            selectedIDs={[objectID]}
            reload={reload}
            fromList={false}
          />
          <RenameDocDialog
            open={renameDialog.open}
            setOpenDialog={setOpenRenameDialog}
            objectID={objectID}
            afterUpdated={(newName: string) => {
              setRenamedName(newName)
              setChanged((c) => c + 1)
            }}
            name={docDetail.docInfo.documentName}
          />
          <CsvConfigDialog
            docType={docInfo!.documentType}
            docTypeName={docInfo!.documentTypeName}
            open={Boolean(openCsvConfigDialog)}
            actionText="出力"
            subGroup={docInfo!.isVerify ? docInfo!.subGroup : undefined}
            ocrBuilderConfigID={docInfo!.ocrBuilderConfigID}
            onClose={(canceled: boolean) => {
              setOpenCsvConfigDialog(false)
              !canceled && doSaveAndDownload(openCsvConfigDialog === 'fromMulti')
            }}
          />
        </>
      )}
    </LoadingWrapper>
  )
}

export default DocumentDetail
